import * as _ from "lodash";
import * as log from "loglevel";
import * as React from "react";

import { observable } from "mobx";
import { inject, observer, Provider } from "mobx-react";
import { Link } from "react-router-dom";
import { Button, Checkbox, Form, Icon, Input } from "semantic-ui-react";
import { DialogService } from "../../services/dialog-service";
import { AppStore } from "../../stores/app-store";
import { App, IconLocation as IconLocations } from "../../stores/models/app";
import { AppAction } from "../../stores/models/app-action";
import { EditorPane } from "../common/editor-pane";
import { ActionEditor } from "./action-editor";

declare var toastr: any;

interface IAppEditorProps{
	appStore?: AppStore;
	app?: App;
	show?: boolean;
	onClose?(): void;
}

interface IAppEditorState{
	errorMessage?: string;
	showActionEditor?: boolean;
	activeAction?: AppAction;
	name: string;
	url: string;
	settingsUrl: string;
	icon: string;
	apiOnly: boolean;
	noDemo: boolean;
	iconLocation: IconLocations;
	description: string;
	permissionDescription: string;
	internal: boolean;
}

@inject("appStore") @observer
export class AppEditor extends React.Component<IAppEditorProps, IAppEditorState>
{
	constructor(props, state){
		super(props, state);

		this.state = {
			"name": "",
			"url": "",
			"settingsUrl": "",
			"icon": "",
			"apiOnly": false,
			"noDemo": false,
			"iconLocation": IconLocations.bottom,
			"description": "",
			"permissionDescription": "",
			"internal": false,
		};
		this.handleClose = this.handleClose.bind(this);
		this.handleTextChange = this.handleTextChange.bind(this);
		this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
		this.handleSave = this.handleSave.bind(this);
		// this.handleSaveDebounced = _.debounce(this.handleSave, 400);
		this.handleRemoveApp = this.handleRemoveApp.bind(this);
		this.handleAddAction = this.handleAddAction.bind(this);
		this.handleEditAction = this.handleEditAction.bind(this);
		this.handleSelectChange = this.handleSelectChange.bind(this);
		this.handleRemoveActions = this.handleRemoveActions.bind(this);
		this.handleActionItemClick = this.handleActionItemClick.bind(this);
		this.handleCloseActionEditor = this.handleCloseActionEditor.bind(this);
	}

	componentWillReceiveProps(nextProps){
		this.setAppState(nextProps.app);
	}

	async setAppState(app: App){
		if(app){
			if(app.id){
				try {
					await Promise.all([app.loadKeys(), app.loadActions()]);
				} catch (err) {
					log.error(err);
					this.setState({"errorMessage": err.message});
				}
			}
			console.log(`setAppState:`, app);
			this.setState({
				"name": app.name || "",
				"url": app.url || "",
				"settingsUrl": app.settingsUrl || "",
				"icon": app.icon || "",
				"apiOnly": app.apiOnly || false,
				"noDemo": app.noDemo || false,
				"iconLocation": app.iconLocation || IconLocations.bottom,
				"description": app.description || "",
				"permissionDescription": app.permissionDescription || "",
				"internal": app.internal || false,
			});
		}
		else{
			this.setState({
				"name": "",
				"url": "",
				"settingsUrl": "",
				"icon": "",
				"apiOnly": false,
				"noDemo": false,
				"iconLocation": IconLocations.bottom,
				"description": "",
				"internal": false,
			});
		}
	}

	handleClose(){
		if(this.props.onClose){
			this.props.onClose();
		}
	}

	handleTextChange(e: any, {name, value}){
		this.setState({[name]: value} as any);
		this.props.app.updateProperty(name, value);
	}

	handleCheckboxChange(e: any, {name}){
		let value = !this.state[name];
		this.setState({[name]: value} as any);
		this.props.app.updateProperty(name, value);
	}

	handleSelectChange(e: any, {name, value}){
		console.log(name, value);
		this.setState({[name]: value} as any);
		this.props.app.updateProperty(name, value);
	}

	async handleSave(ev){
		ev.stopPropagation();
		ev.preventDefault();
		try {
			let a = await this.props.appStore.save(this.props.app);
			await this.setAppState(this.props.app);
			toastr.success("Change Saved");
			this.setState({"errorMessage": null});
		} catch (err) {
			console.error(err);
			this.setState({"errorMessage": err.message});
		}
	}

	async handleRemoveApp(e){
		e.preventDefault();
		e.stopPropagation();
		try {
			let result = await DialogService.showConfirm("Are you sure?", "This action will delete the app");
			if(result){
				await this.props.appStore.remove(this.props.app);
				this.handleClose();
			}
			this.setState({"errorMessage": null});
		} catch (err) {
			this.setState({"errorMessage": err.message});
			console.error(err);
		}
	}

	async handleAddAction(){
		try{
			await this.props.app.addAction();
			this.setState({"errorMessage": null});
		} catch (err) {
			this.setState({"errorMessage": err.message});
			console.error(err);
		}
	}

	async handleRemoveActions(){
		try {
			let acts = [];
			_.filter(this.props.app.actions, {"selected": true}).forEach(act => {
				acts.push(act);
			});
			await this.props.app.removeActions(acts);
		} catch (err) {
			this.setState({"errorMessage": err.message});
			console.error(err);
		}
	}

	handleEditAction(action: AppAction){
		this.setState({"showActionEditor": true, "activeAction": action});
	}

	handleActionItemClick(event: any, action: AppAction){
		action.toggleSelection();
	}

	handleCloseActionEditor(){
		this.setState({"showActionEditor": false});
	}

	render(){
		let acts = null;
		if(this.props.app && this.props.app.actions){
			acts = this.props.app.globalActions;
		}
		console.log(`render:`, this.state);
		return (
			<div>
				<EditorPane title="Application Editor" show={this.props.show} onClose={this.handleClose}>
					{this.props.app &&
						<div>
							<Form onSubmit={this.handleSave}>
								<Form.Field>
									<Form.Input required type="text" label="Name" name="name" value={this.state.name} onChange={this.handleTextChange}  />
								</Form.Field>
								<Form.Field>
									<Form.Input required type="text" label="Url" name="url" value={this.state.url} onChange={this.handleTextChange} />
								</Form.Field>
								<Form.Field>
									<Form.Input type="text" label="Settings Url" name="settingsUrl" value={this.state.settingsUrl} onChange={this.handleTextChange} />
								</Form.Field>
								<Form.Field>
									<Form.Input required type="text" label="Icon" name="icon" value={this.state.icon} onChange={this.handleTextChange} />
								</Form.Field>
								<Form.Field>
									<Form.TextArea type="text" label="Description" name="description" value={this.state.description} onChange={this.handleTextChange} />
								</Form.Field>
								<Form.Field>
									<Form.TextArea type="text" label="Permission Description (for info blurb)" name="permissionDescription" value={this.state.permissionDescription} onChange={this.handleTextChange} />
								</Form.Field>
								<Form.Field>
										<Form.Checkbox label="API only" toggle name="apiOnly" checked={this.state.apiOnly} onChange={this.handleCheckboxChange} />
								</Form.Field>
								<Form.Field>
										<Form.Checkbox label="Internal only" toggle name="internal" checked={this.state.internal} onChange={this.handleCheckboxChange} />
								</Form.Field>
								<Form.Field>
										<Form.Checkbox label="Don't show on demo" toggle name="noDemo" checked={this.state.noDemo} onChange={this.handleCheckboxChange} />
								</Form.Field>
								<Form.Field>
									<Form.Select label="Icon Location" name="iconLocation" options={[{"key": IconLocations.bottom, "text": "Bottom", "value": IconLocations.bottom}, {"key": IconLocations.middleRight, "text": "middleRight", "value": IconLocations.middleRight}]} value={this.state.iconLocation} onChange={this.handleSelectChange} />
								</Form.Field>
								<Form.Field>
									<label>App Key</label>
									{this.props.app.appKey}
								</Form.Field>
								<Form.Field>
									<label>Secret Key</label>
									{this.props.app.secretKey}
								</Form.Field>
								<Form.Group>
									<Button type="submit" icon className="jibunu-green" labelPosition="left">
										<Icon name="save" />
										Save
									</Button>
									<Button icon className="jibunu-blue" labelPosition="left" onClick={this.handleClose}>
										<Icon name="remove" />
										Close
									</Button>
									<Button icon className="jibunu-red" labelPosition="left" onClick={this.handleRemoveApp}>
										<Icon name="trash" />
										Delete
									</Button>
								</Form.Group>
							</Form>
							<br /><br />
						</div>
					}
					{this.props.app && this.props.app.id &&
						<div>
							<span className="list-header">Actions</span>
							<button className="circular ui icon button blue" onClick={this.handleAddAction} title="Add Action">
								<i className="icon plus" />
							</button>
							<button disabled={_.filter(acts, {"selected": true}).length == 0} onClick={this.handleRemoveActions}  className="circular ui icon button red" title="Delete Selected">
								<i className="icon trash" />
							</button>
							<div className="ui big divided list">
								{acts && acts.map((act: AppAction) => {
												return(
													<div key={act.id} className={act.selected ? "active item" : "item"}>
														<input type="checkbox" checked={act.selected} onChange={(e) => this.handleActionItemClick(e, act)} />&nbsp;&nbsp;
														<a onClick={() => this.handleEditAction(act)}>{act.name}</a>
														&nbsp;&nbsp;&nbsp;&nbsp;<span>{act.description}</span>
													</div>
												);
											})
									}
							</div>
						</div>
					}
					{this.state.errorMessage &&
							<div className="ui red icon message">
								<i className="warning sign icon"></i>
								{this.state.errorMessage}
							</div>
						}
				</EditorPane>
				<ActionEditor show={this.state.showActionEditor} app={this.props.app} action={this.state.activeAction} onClose={this.handleCloseActionEditor} />
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./src/views/apps/app-editor.tsx